import React, { forwardRef, useState, useEffect, ReactElement, VideoHTMLAttributes } from 'react'
import api from 'helpers/api'
import { isMortaUploadedUrl, validateSourceUrl } from 'helpers/utils'
import { getAuthToken } from 'helpers/auth'
import { useApplicationStore } from 'hooks/application'

export interface VideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
  blockProps: { src: string }
}

export default forwardRef<HTMLVideoElement, VideoProps>(function EmbeddedVideo(props, ref): ReactElement {
  const { displayErrorMessage } = useApplicationStore()

  const { blockProps, style, ...elementProps } = props

  const [mortaSrc, setMortaSrc] = useState<string | undefined>()
  const [ready, setReady] = useState(false)

  const signUrl = async () => {
    const token = getAuthToken()
    if (ready || !token) return

    if (!blockProps.src) {
      setReady(true)
      return
    }

    try {
      const signedFileTokenResponse = await api.signUrl(blockProps.src)
      const xhr = new XMLHttpRequest()
      xhr.open('GET', signedFileTokenResponse.data.url)
      xhr.responseType = 'blob'
      xhr.setRequestHeader('Authorization', token)
      xhr.send()

      xhr.onload = () => {
        if (xhr.readyState === xhr.DONE) {
          if (xhr.status === 200) {
            const file = window.URL.createObjectURL(xhr.response)
            setMortaSrc(file)
            setReady(true)
          } else {
            console.error('Something went wrong')
            setReady(true)
          }
        }
      }
    } catch (err) {
      displayErrorMessage(err)
      setReady(true)
      setMortaSrc(undefined)
      return
    }
  }

  useEffect(() => {
    if (blockProps.src && isMortaUploadedUrl(blockProps.src)) {
      signUrl().catch(() => {
        // Unhandled rejection is handled by displayErrorMessage,
        // this is just to prevent the console error.
      })
    } else {
      setReady(true)
    }
  }, [])

  if (!ready) {
    return <span>Loading video...</span>
  }

  if (!mortaSrc) {
    return <span>Video not available</span>
  }
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video ref={ref} {...elementProps} controls style={style} {...elementProps}>
      <source src={validateSourceUrl(mortaSrc)} />
    </video>
  )
})
